import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { COMPANY } from "../mock/mock";
import { Phone, Mail, MapPin, Clock, Send, Facebook, Instagram } from "lucide-react";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { inquiriesApi } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";

export default function Contact() {
  const { t } = useI18n();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await inquiriesApi.create({ type: "contact", ...form });
      toast({ title: t("contact.toastTitle"), description: t("contact.toastDesc") });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast({ title: t("car.sendFailed"), description: t("car.sendFailedDesc") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#1a1a1a] text-white min-h-screen">
      <Navbar />
      <Toaster />

      <section className="pt-[140px] pb-16 px-5 lg:px-8">
        <div className="max-w-[1200px] mx-auto text-center">
          <div className="text-[#c5a76f] text-sm tracking-[0.3em] uppercase mb-4">{t("contact.overline")}</div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-5" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
            {t("contact.title")} <span className="text-[#c5a76f]">{t("contact.titleAccent")}</span> {t("contact.titleEnd")}
          </h1>
          <p className="text-white/70 max-w-2xl mx-auto">
            {t("contact.intro")}
          </p>
        </div>
      </section>

      <section className="px-5 lg:px-8 pb-20">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: Phone, title: t("contact.phone"), value: COMPANY.phone, href: `tel:${COMPANY.phone}` },
              { icon: Mail, title: t("contact.email"), value: COMPANY.email, href: `mailto:${COMPANY.email}` },
              { icon: MapPin, title: t("contact.showroom"), value: `${COMPANY.address.street}, ${COMPANY.address.zip} ${COMPANY.address.city}`, href: "https://maps.google.com/?q=Kleiweg+27+9340+Lede" },
              { icon: Clock, title: t("contact.hours"), value: t("contact.hoursValue") },
            ].map((c) => {
              const Inner = (
                <div className="flex gap-4 p-6 bg-[#242424] border border-white/5 hover:border-[#c5a76f]/40 rounded-md transition-colors">
                  <div className="w-12 h-12 rounded-full bg-[#c5a76f]/10 border border-[#c5a76f]/30 flex items-center justify-center flex-shrink-0">
                    <c.icon className="w-5 h-5 text-[#c5a76f]" />
                  </div>
                  <div>
                    <div className="text-white/50 text-xs uppercase tracking-wider mb-1">{c.title}</div>
                    <div className="text-white font-medium">{c.value}</div>
                  </div>
                </div>
              );
              return c.href ? (
                <a key={c.title} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
                  {Inner}
                </a>
              ) : (
                <div key={c.title}>{Inner}</div>
              );
            })}

            <a
              href={`https://wa.me/${COMPANY.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-button"
              className="flex items-center gap-3 w-full px-6 py-4 bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold rounded-md transition-colors group"
            >
              <WhatsAppIcon className="w-6 h-6" />
              <span className="flex flex-col text-left">
                <span className="text-xs uppercase tracking-wider text-white/80">WhatsApp</span>
                <span className="text-base">{t("contact.whatsappCta")}</span>
              </span>
            </a>

            <div className="flex gap-3 pt-2">
              <a href={COMPANY.social.facebook} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full border border-[#c5a76f]/40 flex items-center justify-center text-[#c5a76f] hover:bg-[#c5a76f] hover:text-black transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={COMPANY.social.instagram} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full border border-[#c5a76f]/40 flex items-center justify-center text-[#c5a76f] hover:bg-[#c5a76f] hover:text-black transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          <form onSubmit={onSubmit} className="lg:col-span-3 bg-[#242424] border border-white/5 rounded-md p-7 lg:p-10 space-y-5">
            <h2 className="text-2xl text-white font-bold" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
              {t("contact.formTitle")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-white/70 text-xs uppercase tracking-wider mb-2">{t("common.name")}</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-white/70 text-xs uppercase tracking-wider mb-2">{t("common.email")}</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-white/70 text-xs uppercase tracking-wider mb-2">{t("common.phone")}</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white" />
              </div>
              <div>
                <label className="block text-white/70 text-xs uppercase tracking-wider mb-2">{t("common.subject")}</label>
                <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white" />
              </div>
            </div>
            <div>
              <label className="block text-white/70 text-xs uppercase tracking-wider mb-2">{t("common.message")}</label>
              <textarea required rows="5" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white" />
            </div>
            <button disabled={loading} className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#c5a76f] text-black hover:bg-[#d8bd86] transition-colors text-sm font-semibold tracking-widest uppercase disabled:opacity-60 rounded">
              {loading ? t("common.sending") : t("common.submit")} <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>

      <section className="px-5 lg:px-8 pb-24">
        <div className="max-w-[1400px] mx-auto rounded-md overflow-hidden border border-white/10 h-[480px]">
          <iframe
            title="Map"
            src="https://maps.google.com/maps?q=Kleiweg%2027%209340%20Lede%20Belgium&t=&z=15&ie=UTF8&iwloc=&output=embed"
            className="w-full h-full grayscale-[40%]"
            loading="lazy"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}
